import { useState } from 'react';
import { toast } from 'sonner';
import { Plus, Trash, Sparkle, Lightning, WarningCircle, CheckCircle } from '@phosphor-icons/react';
import { useLanguage } from '@/context/LanguageContext';
import { useAuth } from '@/context/AuthContext';
import { runToolJob } from '@/lib/toolJob';
import useDocumentMeta from '@/hooks/useDocumentMeta';
import { TOOL_SEO, toolPath } from '@/lib/toolRoutes';
import ToolLayout, { Card, Field, Input, Textarea, Select, PrimaryButton, ScoreBadge, StatusDot, UpsellBanner } from './_ToolLayout';
import EmailCaptureCard from '@/components/EmailCaptureCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTORS = ['Moda & Tekstil', 'Ev & Yaşam', 'Elektronik', 'Kozmetik & Kişisel Bakım', 'Gıda & İçecek', 'Sağlık & Wellness', 'Otomotiv & Yedek Parça', 'Bebek & Anne', 'Spor & Outdoor', 'Hobi & Oyun', 'Diğer'];
const MARKETS = ['Türkiye', 'Türkiye + Avrupa', 'Türkiye + ABD', 'Türkiye + MENA', 'Global'];

export default function GeoCheckupTool() {
  const { t, language } = useLanguage();
  const { getAuthHeaders } = useAuth();
  const seo = TOOL_SEO.geo[language] || TOOL_SEO.geo.tr;
  useDocumentMeta({
    title: seo.title,
    description: seo.description,
    canonical: toolPath('geo', language),
    alternates: { tr: toolPath('geo', 'tr'), en: toolPath('geo', 'en'), 'x-default': toolPath('geo', 'tr') },
    ogKind: 'tool',
  });
  const [form, setForm] = useState({ sirket: '', url: '', sektor: 'Moda & Tekstil', ulke: 'Türkiye', aciklama: '' });
  const [rivals, setRivals] = useState(['']);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const addRival = () => rivals.length < 5 && setRivals([...rivals, '']);
  const updateRival = (i, v) => setRivals((r) => r.map((x, idx) => (idx === i ? v : x)));
  const removeRival = (i) => setRivals((r) => r.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!form.sirket.trim()) { toast.error(t('tools.geo.required.sirket')); return; }
    setLoading(true);
    setResult(null);
    try {
      const filteredRivals = rivals.map((x) => x.trim()).filter(Boolean);
      const data = await runToolJob(`${API}/tools/geo-checkup`, { ...form, rakipler: filteredRivals }, getAuthHeaders());
      setResult(data);
      window.scrollTo({ top: document.getElementById('geo-result')?.offsetTop || 0, behavior: 'smooth' });
    } catch (e) {
      const msg = e?.response?.data?.detail || t('tools.geo.fail');
      toast.error(typeof msg === 'string' ? msg : t('tools.geo.fail'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <ToolLayout
      testId="geo-checkup-tool"
      tourId="geo"
      title={t('tools.geo.title')}
      subtitle={t('tools.geo.subtitle')}
    >
      {/* Form */}
      <Card className="mb-8">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label={t('tools.geo.field.sirket')} required>
            <Input data-testid="geo-input-sirket" placeholder="GrowthRIQ" value={form.sirket} onChange={(e) => update('sirket', e.target.value)} />
          </Field>
          <Field label={t('tools.geo.field.url')}>
            <Input data-testid="geo-input-url" placeholder="https://" value={form.url} onChange={(e) => update('url', e.target.value)} />
          </Field>
          <Field label={t('tools.geo.field.sektor')} required>
            <Select data-testid="geo-select-sektor" value={form.sektor} onChange={(e) => update('sektor', e.target.value)}>
              {SECTORS.map((s) => <option key={s} value={s} className="bg-[#0F1B36]">{s}</option>)}
            </Select>
          </Field>
          <Field label={t('tools.geo.field.market')}>
            <Select data-testid="geo-select-ulke" value={form.ulke} onChange={(e) => update('ulke', e.target.value)}>
              {MARKETS.map((m) => <option key={m} value={m} className="bg-[#0F1B36]">{m}</option>)}
            </Select>
          </Field>
        </div>
        <Field label={t('tools.geo.field.aciklama')} hint={t('tools.geo.field.aciklama.hint')}>
          <Textarea data-testid="geo-input-aciklama" placeholder="..." value={form.aciklama} onChange={(e) => update('aciklama', e.target.value)} />
        </Field>
        <Field label={t('tools.geo.field.rivals')} hint={t('tools.geo.field.rivals.hint')}>
          <div className="space-y-2" data-testid="geo-rivals">
            {rivals.map((r, i) => (
              <div key={i} className="flex gap-2">
                <Input data-testid={`geo-rival-${i}`} placeholder={t('tools.geo.field.rivals.placeholder')} value={r} onChange={(e) => updateRival(i, e.target.value)} />
                {rivals.length > 1 && (
                  <button onClick={() => removeRival(i)} className="px-3 text-white/60 hover:text-red-400" data-testid={`geo-rival-remove-${i}`}>
                    <Trash size={18} />
                  </button>
                )}
              </div>
            ))}
            {rivals.length < 5 && (
              <button onClick={addRival} className="text-sm text-[#3CD9B8] flex items-center gap-1" data-testid="geo-rival-add">
                <Plus size={14} /> {t('tools.geo.field.rivals.add')}
              </button>
            )}
          </div>
        </Field>

        <PrimaryButton onClick={submit} disabled={loading} data-testid="geo-submit">
          {loading ? <><Sparkle size={18} className="animate-pulse" /> {t('tools.geo.submitting')}</> : <><Sparkle size={18} weight="fill" /> {t('tools.geo.submit')}</>}
        </PrimaryButton>
        {loading && <p className="text-xs text-white/50 mt-3">{t('tools.geo.hint.duration')}</p>}
      </Card>

      {/* Result */}
      {result && <ResultSection result={result} />}
    </ToolLayout>
  );
}

function ResultSection({ result }) {
  const { t } = useLanguage();
  const subs = result.alt_skorlar || {};
  return (
    <div id="geo-result" data-testid="geo-result" className="space-y-6">
      <Card>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
          <div>
            <p className="text-xs text-white/50 uppercase tracking-wider mb-1">{t('tools.geo.result.score')}</p>
            <h2 className="text-2xl font-bold">{result.sirket_adi}</h2>
            <p className="text-sm text-[#3CD9B8] mt-1">{result.readiness_level}</p>
          </div>
          <ScoreBadge score={result.genel_skor} size="lg" />
        </div>
        <p className="text-white/80 leading-relaxed">{result.yonetici_ozeti}</p>
      </Card>

      {/* Sub-scores */}
      <Card>
        <h3 className="text-lg font-semibold mb-4">{t('tools.geo.result.subscores')}</h3>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
          {[
            [t('tools.geo.result.subscore.ai'), subs.ai_gorunurluk],
            [t('tools.geo.result.subscore.tech'), subs.teknik_hazirlik],
            [t('tools.geo.result.subscore.content'), subs.icerik_hazirlik],
            [t('tools.geo.result.subscore.schema'), subs.schema_yapilandirma],
            [t('tools.geo.result.subscore.brand'), subs.marka_otoritesi],
          ].map(([label, val]) => (
            <div key={label} className="text-center">
              <div className="text-2xl font-bold mb-1">{val ?? '—'}</div>
              <div className="text-xs text-white/60">{label}</div>
            </div>
          ))}
        </div>
      </Card>

      {/* Findings */}
      {['teknik_bulgular', 'icerik_bulgular', 'otorite_bulgular'].map((key) => {
        const items = result[key] || [];
        if (!items.length) return null;
        const titleMap = {
          teknik_bulgular: t('tools.geo.result.tech'),
          icerik_bulgular: t('tools.geo.result.content'),
          otorite_bulgular: t('tools.geo.result.brand'),
        };
        return (
          <Card key={key}>
            <h3 className="text-lg font-semibold mb-4">{titleMap[key]}</h3>
            <div className="space-y-3">
              {items.map((it, i) => (
                <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-white/[0.03] border border-white/5">
                  <StatusDot status={it.durum} />
                  <div className="flex-1">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="font-medium text-sm">{it.konu}</span>
                      <span className="text-xs text-white/50">{it.puan}/100</span>
                    </div>
                    <p className="text-xs text-white/70 mb-1">{it.aciklama}</p>
                    <p className="text-xs text-[#3CD9B8]">→ {it.oneri}</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        );
      })}

      {/* Quick wins */}
      {result.hizli_kazanimlar?.length > 0 && (
        <Card>
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2"><Lightning size={20} weight="fill" className="text-[#3CD9B8]" /> {t('tools.geo.result.wins')}</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            {result.hizli_kazanimlar.map((q, i) => (
              <div key={i} className="p-4 rounded-lg bg-[#3CD9B8]/5 border border-[#3CD9B8]/20">
                <div className="flex items-center justify-between text-xs text-white/60 mb-2">
                  <span>#{q.no}</span><span>{q.sure} • {t('tools.geo.result.impact')} {q.etki}</span>
                </div>
                <p className="text-sm text-white/90">{q.aksiyon}</p>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Critical issues */}
      {result.kritik_sorunlar?.length > 0 && (
        <Card>
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2"><WarningCircle size={20} weight="fill" className="text-amber-400" /> {t('tools.geo.result.critical')}</h3>
          <div className="space-y-2">
            {result.kritik_sorunlar.map((s, i) => (
              <div key={i} className="p-3 rounded-lg bg-amber-500/5 border border-amber-500/20">
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="font-medium">#{s.no} {s.sorun}</span>
                  <span className="text-amber-400">{t('tools.geo.result.impact')} {s.etki}</span>
                </div>
                <p className="text-xs text-white/70">{s.aciklama}</p>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Roadmap */}
      {result.yol_haritasi && (
        <Card>
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2"><CheckCircle size={20} weight="fill" className="text-emerald-400" /> {t('tools.geo.result.roadmap')}</h3>
          <div className="grid sm:grid-cols-3 gap-3">
            {[[t('tools.geo.result.roadmap.30'), result.yol_haritasi.gun30], [t('tools.geo.result.roadmap.60'), result.yol_haritasi.gun60], [t('tools.geo.result.roadmap.90'), result.yol_haritasi.gun90]].map(([label, val]) => (
              <div key={label} className="p-4 rounded-lg bg-white/[0.04]">
                <div className="text-[#3CD9B8] font-semibold text-sm mb-2">{label}</div>
                <p className="text-sm text-white/80 leading-relaxed">{val}</p>
              </div>
            ))}
          </div>
        </Card>
      )}

      <UpsellBanner />
      <EmailCaptureCard toolSlug="geo-checkup" resultSnapshot={result} />
    </div>
  );
}
